import React from 'react';
import { Box, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import { useNavigate } from 'react-router-dom';

/**
 * Props for the OSWindow component
 */
interface OSWindowProps {
    /** The title displayed in the window's title bar */
    title: string;
    /** The content to render inside the window */
    children: React.ReactNode;
    /** Optional callback when the window is closed */
    onClose?: () => void;
    /** Whether the window should start in maximized state */
    initialMaximized?: boolean;
}

/**
 * OS-style window component that displays content in a draggable, resizable window.
 * Features a title bar with minimize, maximize, and close buttons.
 *
 * @param props - The props for the component
 * @returns The OS Window UI
 */
const OSWindow: React.FC<OSWindowProps> = ({ title, children, onClose, initialMaximized = true }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const [isMaximized, setIsMaximized] = React.useState(initialMaximized);
    const [hoverControls, setHoverControls] = React.useState(false);

    const handleClose = () => {
        if (onClose) {
            onClose();
        } else {
            navigate('/dashboard');
        }
    };

    const toggleMaximize = () => {
        if (!isMobile) {
            setIsMaximized(!isMaximized);
        }
    };

    const effectiveMaximized = isMobile || isMaximized;
    const shouldMaximizeOnTablet = isTablet && initialMaximized;

    return (
        <Box
            sx={{
                position: 'absolute',
                top: effectiveMaximized || shouldMaximizeOnTablet ? 0 : '50%',
                left: effectiveMaximized || shouldMaximizeOnTablet ? 0 : '50%',
                transform: effectiveMaximized || shouldMaximizeOnTablet ? 'none' : 'translate(-50%, -50%)',
                width: effectiveMaximized || shouldMaximizeOnTablet ? '100vw' : { sm: '90%', md: '85%', lg: '80%' },
                height: effectiveMaximized || shouldMaximizeOnTablet ? '100vh' : { sm: '85%', md: '80%', lg: '75%' },
                minWidth: { xs: '100%', sm: 600, md: 800 },
                minHeight: { xs: '100%', sm: 450, md: 500 },
                bgcolor: 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(30px) saturate(180%)',
                borderRadius: effectiveMaximized || shouldMaximizeOnTablet ? 0 : 4,
                boxShadow: isMobile || shouldMaximizeOnTablet ? 'none' : '0 30px 60px rgba(0,0,0,0.4), inset 0 0 0 1px rgba(255,255,255,0.3)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                transition: isMobile ? 'none' : 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                animation: isMobile ? 'none' : 'windowAppear 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
                zIndex: 20,
                border: effectiveMaximized ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                '@keyframes windowAppear': {
                    from: { opacity: 0, transform: 'translate(-50%, -40%) scale(0.92)', filter: 'blur(10px)' },
                    to: { opacity: 1, transform: 'translate(-50%, -50%) scale(1)', filter: 'blur(0)' }
                }
            }}
        >
            {/* macOS Style Title Bar */}
            <Box
                sx={{
                    height: 52,
                    background: 'rgba(255, 255, 255, 0.4)',
                    backdropFilter: 'blur(20px)',
                    borderBottom: '1px solid rgba(0,0,0,0.08)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center', // Center title
                    px: 3,
                    position: 'relative',
                    userSelect: 'none'
                }}
                onDoubleClick={toggleMaximize}
            >
                {/* Traffic Lights (Left Side) */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1.2,
                        position: 'absolute',
                        left: 20,
                        '&:hover .traffic-icon': { opacity: 1 }
                    }}
                    onMouseEnter={() => setHoverControls(true)}
                    onMouseLeave={() => setHoverControls(false)}
                >
                    {/* Close Button */}
                    <Box
                        onClick={handleClose}
                        sx={{
                            width: 13, height: 13, borderRadius: '50%', bgcolor: '#ff5f57',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', border: '0.5px solid rgba(0,0,0,0.12)',
                            '&:active': { bgcolor: '#bf4942' }
                        }}
                    >
                        <CloseIcon className="traffic-icon" sx={{ fontSize: 9, opacity: 0, transition: 'opacity 0.2s', fontWeight: 900, color: 'rgba(0,0,0,0.5)' }} />
                    </Box>

                    {/* Minimize Button (Disabled functionality for now) */}
                    <Box
                        sx={{
                            width: 13, height: 13, borderRadius: '50%', bgcolor: '#febc2e',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: isMobile ? 'default' : 'pointer', border: '0.5px solid rgba(0,0,0,0.12)',
                            '&:active': { bgcolor: '#be8e22' }
                        }}
                    >
                        <RemoveIcon className="traffic-icon" sx={{ fontSize: 9, opacity: 0, transition: 'opacity 0.2s', fontWeight: 900, color: 'rgba(0,0,0,0.5)' }} />
                    </Box>

                    {/* Maximize Button */}
                    <Box
                        onClick={toggleMaximize}
                        sx={{
                            width: 13, height: 13, borderRadius: '50%', bgcolor: '#28c841',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: isMobile ? 'default' : 'pointer', border: '0.5px solid rgba(0,0,0,0.12)',
                            '&:active': { bgcolor: '#1e9631' }
                        }}
                    >
                        <CropSquareIcon className="traffic-icon" sx={{ fontSize: 8, opacity: 0, transition: 'opacity 0.2s', fontWeight: 900, color: 'rgba(0,0,0,0.5)' }} />
                    </Box>
                </Box>

                {/* Centered Title */}
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 900,
                        color: 'rgba(0,0,0,0.7)',
                        fontFamily: 'Quicksand',
                        letterSpacing: '0.5px',
                        fontSize: '0.95rem',
                        maxWidth: '60%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {title}
                </Typography>
            </Box>

            {/* Content Area */}
            <Box sx={{
                flexGrow: 1,
                overflow: 'auto',
                p: 0,
                bgcolor: 'transparent',
                position: 'relative'
            }}>
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: 'url(/bg-dark.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    zIndex: 0,
                    opacity: 0.05
                }} />
                <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {children}
                </Box>
            </Box>
        </Box>
    );
};

export default OSWindow;
